import { defineMessages, MessageDescriptor } from 'react-intl';
import { reportNameMessages } from '../reports/constants';

/**
 * Centralized AOD trigger/type translation messages.
 * Use with FormattedMessage or formatMessage so the extractor picks up the ids and defaultMessages.
 *
 * @example
 * <FormattedMessage {...triggerMessages[trigger]} />
 */
export const triggerMessages: Record<string, MessageDescriptor> = defineMessages({
  EOW: {
    id: 'audit.aod.rules.trigger.EOW',
    defaultMessage: 'Cierre semanal',
  },
  EOW_AND_ORDER_END: {
    id: 'audit.aod.rules.trigger.EOW_AND_ORDER_END',
    defaultMessage: 'Cierre semanal + fin de orden',
  },
  EOW_AND_ORDER_END_SHORT: {
    id: 'audit.aod.rules.trigger.EOW_AND_ORDER_END_SHORT',
    defaultMessage: 'Cierre + fin',
  },
  MANUAL: {
    id: 'audit.aod.rules.trigger.MANUAL',
    defaultMessage: 'Manual',
  },
  ORDER_END: {
    id: 'audit.aod.rules.trigger.ORDER_END',
    defaultMessage: 'Fin de orden',
  },
});

/**
 * AOD rules list table column headers (defaults from es.json).
 */
export const aodRulesMessages: Record<string, MessageDescriptor> = defineMessages({
  createdAt: {
    id: 'audit.aod.rules.createdAt',
    defaultMessage: 'Creada',
  },
  name: {
    id: 'audit.aod.rules.name',
    defaultMessage: 'Nombre',
  },
  reportType: {
    id: 'audit.aod.rules.reportType',
    defaultMessage: 'Tipo de reporte',
  },
  trigger: {
    id: 'audit.aod.rules.trigger',
    defaultMessage: 'Trigger',
  },
  active: {
    id: 'audit.aod.rules.active',
    defaultMessage: 'Activa',
  },
});

/**
 * Active/inactive status labels for AOD rules list (common.status ids, Spanish defaults).
 */
export const activeStatusMessages: Record<string, MessageDescriptor> = defineMessages({
  active: {
    id: 'common.status.active',
    defaultMessage: 'Activa',
  },
  inactive: {
    id: 'common.status.inactive',
    defaultMessage: 'Inactiva',
  },
});

/**
 * AOD report type labels for list/filters. Re-exports from reports/constants (single source of truth).
 */
export const aodReportTypeMessages: Record<string, MessageDescriptor> = {
  AUDIT_GIP_STATUS: reportNameMessages.AUDIT_GIP_STATUS,
  AUDIT_GIP_ALTERNATIVE_REPORT: reportNameMessages.AUDIT_GIP_ALTERNATIVE_REPORT,
};

/**
 * AOD run status labels for list filters (completed_with_export, failed, etc.).
 */
export const aodRunStatusMessages: Record<string, MessageDescriptor> = defineMessages({
  COMPLETED_WITH_EXPORT: {
    id: 'audit.aod.runs.status.completed_with_export',
    defaultMessage: 'Ejecutada con export',
  },
  COMPLETED_WITHOUT_EXPORT: {
    id: 'audit.aod.runs.status.completed_without_export',
    defaultMessage: 'Ejecutada sin export',
  },
  IN_PROGRESS: {
    id: 'audit.aod.runs.status.in_progress',
    defaultMessage: 'En progreso',
  },
  FAILED: {
    id: 'audit.aod.runs.status.failed',
    defaultMessage: 'Error',
  },
});

/**
 * Labels for `statusData.completedWithoutExport.reason` (why the run did not export).
 */
export const aodRunCompletionReasonMessages: Record<string, MessageDescriptor> = defineMessages({
  field: {
    id: 'audit.aod.runs.field.completionReason',
    defaultMessage: 'Motivo',
  },
  comment: {
    id: 'audit.aod.runs.field.completionComment',
    defaultMessage: 'Comentario',
  },
  LOW_EFFICIENCY: {
    id: 'audit.aod.runs.completionReason.LOW_EFFICIENCY',
    defaultMessage: 'Baja eficiencia',
  },
  MISSING_MATERIALS: {
    id: 'audit.aod.runs.completionReason.MISSING_MATERIALS',
    defaultMessage: 'Materiales faltantes',
  },
});

/**
 * Copy for refresh / reprocess when completion reason is MISSING_MATERIALS.
 */
export const aodMissingMaterialsMessages: Record<string, MessageDescriptor> = defineMessages({
  guidanceTitle: {
    id: 'audit.aod.actions.triggerReprocess.missingMaterials.title',
    defaultMessage: 'Reprocesar no disponible',
  },
  guidanceDescription: {
    id: 'audit.aod.actions.triggerReprocess.missingMaterials.description',
    defaultMessage:
      'Los schedules sin match no tienen medios de spot vinculados. Cargá los materiales y actualizá la ejecución; el reprocesamiento OLAF no ayuda sin audio.',
  },
  refreshDescription: {
    id: 'audit.aod.actions.refreshRun.description.missingMaterials',
    defaultMessage:
      'Después de cargar los materiales faltantes, actualizá la ejecución para re-evaluar el reporte.',
  },
  reprocessError: {
    id: 'audit.aod.actions.triggerReprocess.error.missingMaterials',
    defaultMessage:
      'No se puede reprocesar: faltan materiales. Cargá los audios y actualizá la ejecución.',
  },
  reprocessErrorGeneric: {
    id: 'audit.aod.actions.triggerReprocess.error',
    defaultMessage: 'No se pudo iniciar el reprocesamiento',
  },
});

/**
 * Notification policy labels for when deficiency notifications are sent to managers (Spanish defaults).
 */
export const notificationPolicyMessages: Record<string, MessageDescriptor> = defineMessages({
  label: {
    id: 'audit.aod.rules.notificationPolicy.label',
    defaultMessage: 'Política de notificaciones',
  },
  NONE: {
    id: 'audit.aod.rules.notificationPolicy.NONE',
    defaultMessage: 'Sin notificaciones',
  },
  ORDER_END_ONLY: {
    id: 'audit.aod.rules.notificationPolicy.ORDER_END_ONLY',
    defaultMessage: 'Solo fin de orden',
  },
  ALWAYS: {
    id: 'audit.aod.rules.notificationPolicy.ALWAYS',
    defaultMessage: 'Siempre (cierre semanal y fin de orden)',
  },
});

/**
 * AOD rule wizard / add form messages (Spanish defaults).
 */
export const aodRuleWizardMessages: Record<string, MessageDescriptor> = defineMessages({
  addReportType: {
    id: 'audit.aod.rules.add.reportType',
    defaultMessage: 'Tipo de reporte',
  },
  namePlaceholder: {
    id: 'audit.aod.rules.name.placeholder',
    defaultMessage: 'Ingresa el nombre de la regla',
  },
  managers: {
    id: 'audit.aod.rules.managers',
    defaultMessage: 'Responsables',
  },
  managersPlaceholder: {
    id: 'audit.aod.rules.managers.placeholder',
    defaultMessage: 'Selecciona usuarios',
  },
  managersHelp: {
    id: 'audit.aod.rules.managers.help',
    defaultMessage: 'Selecciona los usuarios que serán responsables de esta regla',
  },
  reviewBasicInfo: {
    id: 'audit.aod.rules.review.basicInfo',
    defaultMessage: 'Información Básica',
  },
});
